import { useState, useEffect, useRef, useCallback } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { FiUpload, FiVideo } from "react-icons/fi";
import { ICONS } from "../constants/icons";
import { useParams, useSearchParams } from "react-router";
import {
  ref,
  onValue,
  push,
  set,
  update,
  get,
  query,
  orderByChild,
  equalTo,
  off
} from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { FiCamera, FiMic } from "react-icons/fi";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";
import { storage } from "../constants/firebase";
import useMoveTo from "../constants/navigation";
import { CgAttachment } from "react-icons/cg";
import CreatePoll from "../components/CreatePoll";
import PollMessage from "../components/PollMessage";
import { v4 as uuidv4 } from "uuid";
import {
  calculateStreakWithFreeze,
  compressImage,
  convertHeicToJpg,
  copyToClipboard,
  formatDate,
  formatTime,
  handleDownloadImage,
  parseMentions,
} from "../utils/utils";
import { BsThreeDotsVertical } from "react-icons/bs";
import useKeyboardOpen from "../hooks/useKeyboardOpen";
import { ROUTES } from "../App";
import ViewMembers from "../components/ViewMembers";
import { MdPoll } from "react-icons/md";
import ReactionBox from "../components/ReactionBox";
import MessageBubble from "../components/MessageBubble";
import MessageInput from "../components/MessageInput";
import RecordingPopup from "../components/RecordingPopup";
import { useChatMessages } from "../hooks/useChatMessages";
import { useChatScroll } from "../hooks/useChatScroll";
import { useMessageActions } from "../hooks/useMessageActions";
import { useRecording } from "../hooks/useRecording";
import { formatGroupedMessages, groupMessagesByDate } from "../utils/chatUtils";
import SignInPopup from "../components/SignInPopup";
import ForwardSharePopup from "../components/ForwardSharePopup";

const CommentItem = ({ comment, currentUser, formatTime, handleDeleteComment, loadedImages, setLoadedImages }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const TEXT_LIMIT = 250;
  const isLongText = comment.text && comment.text.length > TEXT_LIMIT;
  const displayText = isLongText && !isExpanded ? comment.text.slice(0, TEXT_LIMIT) + "..." : comment.text;
  const isFromCurrentUser = comment.senderId === currentUser?.uid;

  return (
    <div className="flex items-start gap-3">
      <div className="relative w-8 h-8 flex-shrink-0">
        {!loadedImages[`comment-${comment.id}`] && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
        )}
        <img 
          src={comment.senderAvatar || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"} 
          className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${loadedImages[`comment-${comment.id}`] ? "opacity-100" : "opacity-0"}`} 
          onLoad={() => setLoadedImages(prev => ({ ...prev, [`comment-${comment.id}`]: true }))}
          alt="" 
        />
      </div>
      <div className={`flex-1 min-w-0 rounded-2xl p-3 shadow-sm ${
        isFromCurrentUser
          ? "bg-primary text-white rounded-tr-none"
          : "bg-gray-100 dark:bg-[#2A2A2A] rounded-tl-none"
      }`}>
        <div className="flex justify-between items-center mb-1">
          <span className={`text-xs font-bold ${isFromCurrentUser ? "text-white" : "text-primary"}`}>
            {comment.senderName}
          </span>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] ${isFromCurrentUser ? "text-gray-200" : "opacity-50"}`}>
              {formatTime(comment.timestamp)}
            </span>
            {isFromCurrentUser && (
              <button 
                onClick={() => handleDeleteComment(comment.id)}
                className="p-1 hover:bg-white/20 dark:hover:bg-red-900/30 rounded-full transition-colors group"
              >
                <svg className="w-3 h-3 text-gray-200 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
              </button>
            )}
          </div>
        </div>
        <p 
            className="text-sm whitespace-pre-wrap"
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
        >
            {displayText}
        </p>
        {isLongText && (
          <button 
              onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
              }} 
              className={`${isFromCurrentUser ? "text-gray-200" : "text-primary dark:text-[#57a1be]"} text-[13px] font-semibold mt-1 hover:underline`}
          >
              {isExpanded ? "Read less" : "Read more"}
          </button>
        )}
      </div>
    </div>
  );
};

const CommunityChat = () => {
  const navigate = useMoveTo();
  const { communityId } = useParams();
  const { currentUser } = useUser();
  const [community, setCommunity] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [openPollModal, setOpenPollModal] = useState(false);
  const [copied, setCopied] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const isKeyboardOpen = useKeyboardOpen();
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [invitePopupOpen, setInvitePopupOpen] = useState(false);
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [existingCommunityInvite, setExistingCommunityInvite] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  // Maps display name → uid for mentions inserted in the current message
  const [mentionUidMap, setMentionUidMap] = useState({});
  const [communityMembers, setCommunityMembers] = useState([]);
  const [loadedImages, setLoadedImages] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);

  // Comments Thread States
  const [commentThread, setCommentThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);

  // Custom hooks
  const {
    messages,
    loading: messagesLoading,
    deleteMessage,
    addReaction,
    removeReaction,
    loadMoreMessages,
    loadingMore,
    noMoreMessages,
  } = useChatMessages("community", communityId, currentUser);

  const { scrollContainerRef, setContainerRef, handleScroll, pullState } =
    useChatScroll(messages, loadMoreMessages);

  const {
    selectionMode,
    setSelectionMode,
    selectedMessage,
    setSelectedMessage,
    selectedMessageId,
    setSelectedMessageId,
    reactionPosition,
    editingMessage,
    cancelEdit,
    editMessageText,
    setEditMessageText,
    replyingTo,
    reactionBoxRef,
    longPressTimeout,
    longPressTriggered,
    isScrollingRef,
    openReactionBox,
    closeReactionBox,
    handleEditMessage,
    handleReplyToMessage,
    clearReply,
    isReactingRef,
  } = useMessageActions(currentUser);

  const {
    isRecording: isVoiceRecording,
    recordingPopup,
    startRecording,
    stopRecording,
    cancelRecording,
    uploadAudio,
  } = useRecording("community", communityId, currentUser);

  // Add this check after all your hooks and before the main return
  useEffect(() => {
    if (!currentUser && community) {
      setShowSignInPopup(true);
    } else {
      setShowSignInPopup(false);
    }
  }, [currentUser, community]);

  // Define sendMessage locally
  const sendMessage = useCallback(
    async (messageData) => {
      if (!communityId || !currentUser) return null;

      const messagesRef = ref(database, `communityMessages/${communityId}`);
      const newMessageRef = push(messagesRef);

      const defaultData = {
        senderId: currentUser.uid,
        timestamp: Date.now(),
        isSeen: { [currentUser.uid]: true },
      };

      await set(newMessageRef, { ...defaultData, ...messageData });
      return newMessageRef.key;
    },
    [communityId, currentUser],
  );

  // Fetch all Zylen users for mentions
  useEffect(() => {
    if (!currentUser) return;

    const usersRef = ref(database, "Users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const usersData = snapshot.val() || {};
      const usersList = [];

      Object.entries(usersData).forEach(([uid, userData]) => {
        // Optionally filter out the current user
        if (userData?.email || userData.email !== null) {
          if (uid !== currentUser?.uid) {
            usersList.push({
              uid: uid,
              name: userData.name || userData.email || "Unknown User",
              profilePic: userData.profilePic,
              // You can add other user properties if needed
            });
          }
        }
      });

      // Sort users by name alphabetically
      usersList.sort((a, b) => a.name.localeCompare(b.name));
      setAllUsers(usersList);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Define editMessage locally
  const editMessage = useCallback(
    async (messageId, newText, mentions = []) => {
      if (!communityId || !currentUser || !messageId || !newText.trim()) return;

      const messageRef = ref(
        database,
        `communityMessages/${communityId}/${messageId}`,
      );

      const updateData = {
        text: newText,
        edited: true,
        editedAt: Date.now(),
      };

      if (mentions.length > 0) {
        updateData.mentions = mentions;
      }

      await update(messageRef, updateData);
    },
    [communityId, currentUser],
  );

  const autoResizeTextarea = (el) => {
    el.style.height = "auto";
    const maxHeight = 112;
    if (el.scrollHeight > maxHeight) {
      el.style.height = maxHeight + "px";
      el.style.overflowY = "auto";
    } else {
      el.style.height = el.scrollHeight + "px";
      el.style.overflowY = "hidden";
    }
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  // Fetch community members
  useEffect(() => {
    if (!communityId) return;

    const membersRef = ref(database, `Communities/${communityId}/members`);
    const unsubscribe = onValue(membersRef, async (snapshot) => {
      const memberIds = snapshot.val() || {};
      const membersList = [];

      for (const memberId of Object.keys(memberIds)) {
        if (memberIds[memberId] === true) {
          const userSnap = await get(ref(database, `Users/${memberId}`));
          const userData = userSnap.val();
          if (userData) {
            membersList.push({
              uid: memberId,
              name: userData.name || userData.email,
              profilePic: userData.profilePic,
            });
          }
        }
      }
      setCommunityMembers(membersList);
    });

    return () => unsubscribe();
  }, [communityId]);

  // Handle input change with mention detection
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewMessage(value);
    setCursorPosition(cursorPos);

    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf("@");

    if (
      atIndex !== -1 &&
      (atIndex === 0 || textBeforeCursor[atIndex - 1] === " ")
    ) {
      const searchText = textBeforeCursor.substring(atIndex + 1);
      const hasSpaceAfter = searchText.includes(" ");

      if (!hasSpaceAfter) {
        setMentionSearch(searchText);
        const filtered = communityMembers.filter(
          (member) =>
            member.uid !== currentUser?.uid &&
            member.name.toLowerCase().includes(searchText.toLowerCase()),
        );
        setMentionSuggestions(filtered);
        setShowMentions(filtered.length > 0);
        setMentionStartIndex(atIndex);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }

    autoResizeTextarea(e.target);
  };

  // Handle mention selection — insert structured @[Name](uid) tag, store uid in map
  const handleMentionSelect = (member) => {
    const beforeMention = newMessage.substring(0, mentionStartIndex);
    const afterMention = newMessage.substring(cursorPosition);
    const structuredTag = `@[${member.name}](${member.uid})`;
    const newText = `${beforeMention}${structuredTag} ${afterMention}`;
    setNewMessage(newText);
    setMentionUidMap((prev) => ({ ...prev, [`${member.name}|${member.uid}`]: member.uid }));
    setShowMentions(false);

    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartIndex + structuredTag.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Check invite token
  useEffect(() => {
    if (!inviteToken || !currentUser || !community) return;

    // If current user is the host, do nothing (don't show invite popup)
    if (currentUser.uid === community.hostId) {
      clearInviteParam(); // Remove invite param from URL
      return;
    }

    const checkMembership = async () => {
      try {
        const memberSnap = await get(
          ref(
            database,
            `Communities/${communityId}/members/${currentUser.uid}`,
          ),
        );

        if (memberSnap.exists()) {
          const isMember = memberSnap.val();
          if (isMember) {
            window.showToast(
              "You are already a member of this community",
              "error",
            );
            clearInviteParam();
          } else {
            setInvitePopupOpen(true);
          }
        } else {
          setInvitePopupOpen(true);
        }
      } catch (err) {
        console.error("Failed to check membership:", err);
        setInvitePopupOpen(true);
      }
    };

    checkMembership();
  }, [inviteToken, currentUser, community, communityId]);

  const handleInviteJoin = async (token) => {
    try {
      const inviteSnap = await get(ref(database, `Invites/${token}`));
      if (!inviteSnap.exists()) {
        window.showToast("Invalid invite link", "error");
        return;
      }

      const { targetType, targetId } = inviteSnap.val();

      if (targetType === "community" && targetId !== communityId) {
        window.showToast("Invite does not match this page", "error");
        return;
      }

      if (targetType === "community") {
        await joinCommunityWithCheck(targetId);
      }
    } catch (err) {
      console.error(err);
      window.showToast("Failed to join", "error");
    }
  };

  const joinCommunityWithCheck = async (communityId) => {
    const memberRef = ref(
      database,
      `Communities/${communityId}/members/${currentUser.uid}`,
    );
    const snap = await get(memberRef);

    if (snap.exists() && snap.val()) {
      window.showToast("You are already a member", "error");
      return;
    }

    const updates = {};
    updates[`Communities/${communityId}/members/${currentUser.uid}`] = true;
    updates[`Users/${currentUser.uid}/joinedCommunities/${communityId}`] = true;

    await update(ref(database), updates);
    window.showToast("Joined community", "success");
    clearInviteParam();
  };

  const clearInviteParam = () => {
    navigate(`${ROUTES.COMMUNITY_CHAT}/${communityId}`, { replace: true });
  };

  useEffect(() => {
    if (currentUser && communityId) {
      const lastSeenRef = ref(
        database,
        `Users/${currentUser.uid}/lastSeen/${communityId}`,
      );
      update(lastSeenRef, { timestamp: Date.now() });
    }
  }, [communityId, currentUser]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingMessage) return;
      if (event.button === 2) return;

      if (!editingMessage) {
        setSelectionMode(false);
        setSelectedMessage(null);
        setSelectedMessageId(null);
      }

      if (
        reactionBoxRef.current &&
        !reactionBoxRef.current.contains(event.target)
      ) {
        setSelectedMessage(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [
    selectedMessageId,
    editingMessage,
    reactionBoxRef,
    setSelectionMode,
    setSelectedMessage,
    setSelectedMessageId,
  ]);

  // Fetch community data
  useEffect(() => {
    if (!communityId) return;

    const communityRef = ref(database, `Communities/${communityId}`);
    const unsubscribeCommunity = onValue(communityRef, (snapshot) => {
      const data = snapshot.val();
      setCommunity(data);
      setIsHost(currentUser?.uid === data?.hostId);
    });

    if (currentUser) {
      const followRef = ref(
        database,
        `Communities/${communityId}/members/${currentUser.uid}`,
      );
      onValue(followRef, (snapshot) => {
        setIsFollowing(!!snapshot.val());
      });
    }

    return () => {
      unsubscribeCommunity();
    };
  }, [communityId, currentUser]);

  // Handle follow/unfollow
  const handleFollow = async () => {
    if (!currentUser || !communityId || !community) return;

    try {
      const updates = {};
      const now = new Date().toISOString();
      const joining = !isFollowing;

      updates[`Communities/${communityId}/members/${currentUser.uid}`] = joining
        ? true
        : null;

      updates[`Users/${currentUser.uid}/joinedCommunities/${communityId}`] =
        joining ? true : null;

      // 🔥 Correct join date logic
      updates[`Communities/${communityId}/memberJoinDates/${currentUser.uid}`] =
        joining ? now : null;

      updates[`Communities/${communityId}/memberCount`] = joining
        ? (community.memberCount || 0) + 1
        : Math.max(0, (community.memberCount || 0) - 1);

      await update(ref(database), updates);
    } catch (error) {
      console.error("Error updating follow status:", error);
    }
  };
  // Format messages
  const formatMessages = (messages) => {
    if (!messages.length) return [];

    const formattedMessages = [];
    let lastSenderId = null;

    messages.forEach((message, index) => {
      const showHeader =
        lastSenderId !== message.senderId ||
        (index > 0 && messages[index - 1].senderId !== message.senderId);

      formattedMessages.push({ ...message, showHeader });
      lastSenderId = message.senderId;
    });

    return formattedMessages;
  };

  const groupedMessages = groupMessagesByDate(messages, formatDate);
  const formattedGroupedMessages = formatGroupedMessages(
    groupedMessages,
    formatMessages,
  );

  // Handle sending message
  const handleSendMessage = async () => {
    // Build structured text
    let structuredText = newMessage;
    Object.entries(mentionUidMap).forEach(([name, uid]) => {
      structuredText = structuredText.replaceAll(
        `@${name}`,
        `@[${name}](${uid})`,
      );
    });

    if (!newMessage.trim() || !currentUser || !communityId) return;

    // RESTRICTION: Only host can post new messages. Others can only reply/comment.
    if (!isHost && !replyingTo) {
      window.showToast("Only the host can post here. You can comment by replying to a post.", "error");
      return;
    }

    try {
      const mentions = parseMentions(newMessage, allUsers, currentUser);
      const communityRef = ref(database, `Communities/${communityId}`);
      const communitySnap = await get(communityRef);
      const communityData = communitySnap.val();

      const lastActivity =
        communityData?.lastActivity && communityData.lastActivity !== ""
          ? Number(communityData.lastActivity)
          : null;

      const streakData = calculateStreakWithFreeze(
        lastActivity,
        communityData?.streakCount ?? 0,
        communityData?.freezeUsed ?? 0,
        2,
      );

      const messageData = {
        text: newMessage,
        senderName: currentUser.name || currentUser.email,
        senderAvatar: currentUser.profilePic || null,
        isHost: currentUser.uid === community?.hostId,
        mentions,
        type: 'post',
        commentCount: 0
      };

      await sendMessage(messageData);

      setTimeout(async () => {
        await update(communityRef, {
          streakCount: streakData.streakCount,
          freezeUsed: streakData.freezeUsed,
          lastActivity: Date.now(),
        });
      }, 500);

      setNewMessage("");
      setMentionUidMap({});
      resetTextareaHeight();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file || !currentUser || !communityId) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
    ];

    if (!allowedTypes.includes(file.type)) {
      window.showToast("Unsupported image format", "error");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let processedFile = file;

      if (file.type === "image/heic" || file.type === "image/heif") {
        processedFile = await convertHeicToJpg(file);
      } else {
        processedFile = await compressImage(file);
      }

      const filename = `${Date.now()}_${processedFile.name}`;
      const imageRef = storageRef(
        storage,
        `communityMessages/${communityId}/${filename}`,
      );

      const uploadTask = uploadBytesResumable(imageRef, processedFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Error uploading image:", error);
          setIsUploading(false);
        },
        async () => {
          try {
            const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
            const communityRef = ref(database, `Communities/${communityId}`);
            const communitySnap = await get(communityRef);
            const communityData = communitySnap.val();

            const lastActivity =
              communityData?.lastActivity && communityData.lastActivity !== ""
                ? Number(communityData.lastActivity)
                : null;

            const streakData = calculateStreakWithFreeze(
              lastActivity,
              communityData?.streakCount ?? 0,
              communityData?.freezeUsed ?? 0,
              2,
            );

            console.log("StreakData: ", streakData);

            await update(communityRef, {
              streakCount: streakData.streakCount,
              freezeUsed: streakData.freezeUsed,
              lastActivity: Date.now(),
            });

            await sendMessage({
              imageUrl,
              senderName: currentUser.name || currentUser.email,
              senderAvatar: currentUser.profilePic || null,
              isHost: currentUser.uid === community?.hostId,
            });

            setIsUploading(false);
            setUploadProgress(0);
          } catch (error) {
            console.error("Error getting download URL:", error);
            setIsUploading(false);
          }
        },
      );
    } catch (error) {
      console.error("Error starting upload:", error);
      window.showToast(`Failed to upload image: ${error.message}`, "error");
      setIsUploading(false);
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file || !currentUser || !communityId) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const filename = `${Date.now()}_${file.name}`;
      const videoRef = storageRef(
        storage,
        `communityMessages/${communityId}/${filename}`,
      );
      const uploadTask = uploadBytesResumable(videoRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Error uploading video:", error);
          setIsUploading(false);
        },
        async () => {
          const videoUrl = await getDownloadURL(uploadTask.snapshot.ref);
          const communityRef = ref(database, `Communities/${communityId}`);
          const communitySnap = await get(communityRef);
          const communityData = communitySnap.val();

          const lastActivity =
            communityData?.lastActivity && communityData.lastActivity !== ""
              ? Number(communityData.lastActivity)
              : null;

          const streakData = calculateStreakWithFreeze(
            lastActivity,
            communityData?.streakCount ?? 0,
            communityData?.freezeUsed ?? 0,
            2,
          );

          await update(communityRef, {
            streakCount: streakData.streakCount,
            freezeUsed: streakData.freezeUsed,
            lastActivity: Date.now(),
          });

          await sendMessage({
            videoUrl,
            senderName: currentUser.name || currentUser.email,
            senderAvatar: currentUser.profilePic || null,
            isHost: currentUser.uid === community?.hostId,
          });

          setIsUploading(false);
          setUploadProgress(0);
        },
      );
    } catch (error) {
      console.error("Error uploading video:", error);
      window.showToast(`Failed to upload video: ${error.message}`, "error");
      setIsUploading(false);
    }
  };

  const handleAudioFileUpload = async (file) => {
    if (!file || !currentUser || !communityId) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const filename = `${Date.now()}_${file.name}`;
      const audioRef = storageRef(
        storage,
        `communityMessages/${communityId}/${filename}`,
      );
      const uploadTask = uploadBytesResumable(audioRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Error uploading audio:", error);
          setIsUploading(false);
        },
        async () => {
          const audioUrl = await getDownloadURL(uploadTask.snapshot.ref);
          const communityRef = ref(database, `Communities/${communityId}`);
          const communitySnap = await get(communityRef);
          const communityData = communitySnap.val();

          const lastActivity =
            communityData?.lastActivity && communityData.lastActivity !== ""
              ? Number(communityData.lastActivity)
              : null;

          const streakData = calculateStreakWithFreeze(
            lastActivity,
            communityData?.streakCount ?? 0,
            communityData?.freezeUsed ?? 0,
            2,
          );

          await update(communityRef, {
            streakCount: streakData.streakCount,
            freezeUsed: streakData.freezeUsed,
            lastActivity: Date.now(),
          });

          await sendMessage({
            audioUrl,
            senderName: currentUser.name || currentUser.email,
            senderAvatar: currentUser.profilePic || null,
            isHost: currentUser.uid === community?.hostId,
          });

          setIsUploading(false);
          setUploadProgress(0);
        },
      );
    } catch (error) {
      console.error("Error uploading audio:", error);
      window.showToast(`Failed to upload audio: ${error.message}`, "error");
      setIsUploading(false);
    }
  };

  const createPoll = async ({ question, options, allowMultiple }) => {
    if (!currentUser || !communityId) return;

    const pollId = uuidv4();
    const optionsArr = options.map((text, index) => ({
      id: uuidv4(),
      text,
      order: index,
    }));

    const communityRef = ref(database, `Communities/${communityId}`);
    const communitySnap = await get(communityRef);
    const communityData = communitySnap.val();

    const lastActivity =
      communityData?.lastActivity && communityData.lastActivity !== ""
        ? Number(communityData.lastActivity)
        : null;

    const streakData = calculateStreakWithFreeze(
      lastActivity,
      communityData?.streakCount ?? 0,
      communityData?.freezeUsed ?? 0,
      2,
    );

    await update(communityRef, {
      streakCount: streakData.streakCount,
      freezeUsed: streakData.freezeUsed,
      lastActivity: Date.now(),
    });

    const pollRef = ref(database, `polls/${communityId}/${pollId}`);
    await set(pollRef, {
      question,
      allowMultiple,
      options: optionsArr,
      createdBy: currentUser.uid,
      timestamp: Date.now(),
    });

    await sendMessage({
      type: "poll",
      pollId,
      senderName: currentUser.name || currentUser.email,
      senderAvatar: currentUser.profilePic || null,
      isHost: currentUser.uid === community?.hostId,
    });
  };

  // Load existing invite
  useEffect(() => {
    if (!currentUser || !communityId) return;

    const loadExistingInvite = async () => {
      try {
        const targetType = "community";
        const compositeKey = `${targetType}_${communityId}_${currentUser.uid}`;
        const invitesRef = ref(database, "Invites");
        const inviteQuery = query(
          invitesRef,
          orderByChild("compositeKey"),
          equalTo(compositeKey),
        );

        const snapshot = await get(inviteQuery);

        if (snapshot.exists()) {
          const inviteKey = Object.keys(snapshot.val())[0];
          const inviteUrl = `${window.location.origin}/#/community/${communityId}?invite=${inviteKey}`;
          setExistingCommunityInvite(inviteUrl);
        }
      } catch (err) {
        console.error("Community invite preload error:", err);
      }
    };

    loadExistingInvite();
  }, [currentUser.uid, communityId, currentUser]);

  const handleInvite = () => {
    if (!currentUser || !communityId) return;

    if (existingCommunityInvite) {
      copyToClipboard(existingCommunityInvite)
        .then(() => {
          setCopied("invited");
          setTimeout(() => setCopied(""), 2000);
          window.showToast("Invite link copied", "success");
        })
        .catch(() => window.showToast("Failed to copy invite link", "error"));
      return;
    }

    const targetType = "community";
    const targetId = communityId;
    const compositeKey = `${targetType}_${targetId}_${currentUser.uid}`;
    const invitesRef = ref(database, "Invites");

    const tempInviteRef = push(invitesRef);
    const inviteToken = tempInviteRef.key;
    const inviteUrl = `${window.location.origin}/#/community/${targetId}?invite=${inviteToken}`;

    copyToClipboard(inviteUrl)
      .then(() => {
        setCopied("invited");
        setTimeout(() => setCopied(""), 2000);
        window.showToast("Invite link copied", "success");
      })
      .catch(() => window.showToast("Failed to copy invite link", "error"));

    setExistingCommunityInvite(inviteUrl);

    (async () => {
      try {
        await set(tempInviteRef, {
          targetType,
          targetId,
          createdBy: currentUser.uid,
          compositeKey,
          createdAt: Date.now(),
        });
      } catch (error) {
        console.error("Invite save error:", error);
      }
    })();
  };

  const handleReaction = async (messageId, emoji) => {
    await addReaction(messageId, emoji.id);
    setSelectedMessage(null);
    setSelectionMode(false);
  };

  const toggleReaction = async (messageId, emojiId, currentReaction) => {
    if (currentReaction === emojiId) {
      await removeReaction(messageId);
    } else {
      await addReaction(messageId, emojiId);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!isHost) {
      console.warn("Not host, cannot delete");
      return;
    }
    console.log("Message: id: ", messageId);
    await deleteMessage(messageId);
    setSelectionMode(false);
    setSelectedMessageId(null);
  };

  const handleUpdateMessage = async () => {
    if (
      !editMessageText.trim() ||
      !currentUser ||
      !communityId ||
      !editingMessage
    )
      return;

    try {
      const mentions = parseMentions(
        editMessageText,
        communityMembers,
        currentUser,
      );
      await editMessage(editingMessage.id, editMessageText, mentions);
      cancelEdit();
      setSelectionMode(false);
      setNewMessage("");
      resetTextareaHeight();
    } catch (error) {
      console.error("Error updating message:", error);
      window.showToast("Failed to update message", "error");
    }
  };

  const [newMessage, setNewMessage] = useState("");

  const handleGoBack = () => {
    // Try to go back, if there's no history, go to Chats
    if (window.history.length > 2) {
      // >2 because typically there's at least the current page and the previous
      navigate(-1);
    } else {
      navigate(ROUTES.DASHBOARD);
    }
  };

  const handleForwardShare = async (selectedItems) => {
    try {
      // Get the invite link for the current community
      let shareLink = existingCommunityInvite;
      let inviteToken = null;

      // If no existing invite link, create one
      if (!shareLink) {
        const targetType = "community";
        const targetId = communityId;
        const compositeKey = `${targetType}_${targetId}_${currentUser.uid}`;
        const invitesRef = ref(database, "Invites");

        const tempInviteRef = push(invitesRef);
        inviteToken = tempInviteRef.key;
        shareLink = `${window.location.origin}/#/community/${targetId}?invite=${inviteToken}`;

        await set(tempInviteRef, {
          targetType,
          targetId,
          createdBy: currentUser.uid,
          compositeKey,
          createdAt: Date.now(),
        });

        setExistingCommunityInvite(shareLink);
      }

      // Prepare share message
      const shareMessage = `Follow this link to join my zylen community: \n${shareLink}`;

      // Share to selected items
      for (const item of selectedItems) {
        if (item.type === "chat") {
          // Send to private chat
          const chatId = [currentUser.uid, item.id].sort().join("_");
          const messagesRef = ref(database, `privateChats/${chatId}`);
          const newMessageRef = push(messagesRef);

          await set(newMessageRef, {
            senderId: currentUser.uid,
            text: shareMessage,
            timestamp: Date.now(),
            seen: false,
            type: "forwarded",
            originalCommunity: {
              id: communityId,
              name: community.name,
              photo:
                community.imageUrl ||
                "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346",
              inviteToken: inviteToken,
            },
          });
        } else if (item.type === "group") {
          // Send to group chat
          const messagesRef = ref(database, `groupChats/${item.id}`);
          const newMessageRef = push(messagesRef);

          await set(newMessageRef, {
            senderId: currentUser.uid,
            text: shareMessage,
            timestamp: Date.now(),
            seenBy: { [currentUser.uid]: true },
            type: "forwarded",
            originalCommunity: {
              id: communityId,
              name: community.name,
              photo:
                community.imageUrl ||
                "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346",
              inviteToken: inviteToken,
            },
          });
        } else if (item.type === "community") {
          // Send to community chat
          const messagesRef = ref(database, `communityMessages/${item.id}`);
          const newMessageRef = push(messagesRef);

          await set(newMessageRef, {
            senderId: currentUser.uid,
            text: shareMessage,
            timestamp: Date.now(),
            isSeen: { [currentUser.uid]: true },
            type: "forwarded",
            originalCommunity: {
              id: communityId,
              name: community.name,
              photo:
                community.imageUrl ||
                "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346",
              inviteToken: inviteToken,
            },
          });
        }
      }

      window.showToast(`Shared to ${selectedItems.length} chat(s)`, "success");
    } catch (error) {
      console.error("Error sharing community:", error);
      window.showToast("Failed to share community", "error");
    }
  };

  useEffect(() => {
    console.log("No More Messages updated:", noMoreMessages);
  }, [noMoreMessages]);


  const menuItems = [
    {
      label: "Share Community",
      onClick: () => {
        setIsMenuOpen(false);
        setIsSharePopupOpen(true);
      },
    },
    {
      label: copied === "invited" ? "Copied!" : "Copy Invite Link",
      onClick: handleInvite,
    },
  ];

  const closeComments = () => {
    setCommentThread(null);
    setNewComment("");
    setIsPreviewExpanded(false);
  };

  // Fetch comments when thread opens
  useEffect(() => {
    if (!commentThread || !communityId) {
      setComments([]);
      return;
    }

    setIsCommentsLoading(true);
    
    // Safety timeout: stop spinner after 5 seconds even if Firebase is hanging
    const timeout = setTimeout(() => {
      setIsCommentsLoading(prev => {
        if (prev) console.warn("Comment listener timed out after 5s");
        return false;
      });
    }, 5000);

    const commentsRef = ref(database, `communityPostComments/${communityId}/${commentThread.id}`);
    const unsubscribe = onValue(commentsRef, (snapshot) => {
      clearTimeout(timeout);
      const data = snapshot.val();
      if (data) {
        const commentsList = Object.entries(data).map(([id, val]) => ({
          id,
          ...val,
        }));
        setComments(commentsList.sort((a, b) => a.timestamp - b.timestamp));
      } else {
        setComments([]);
      }
      setIsCommentsLoading(false);
    }, (error) => {
      clearTimeout(timeout);
      console.error("Comment listener error:", error);
      setIsCommentsLoading(false);
    });

    return () => {
        clearTimeout(timeout);
        off(commentsRef);
    };
  }, [commentThread, communityId]);

  const handlePostComment = async () => {
    if (!newComment.trim() || !currentUser || !commentThread) return;

    const commentText = newComment; // Store for clear
    setNewComment(""); // Optimistically clear input

    const commentData = {
      text: commentText,
      senderId: currentUser.uid,
      senderName: currentUser.name || currentUser.email,
      senderAvatar: currentUser.profilePic || null,
      timestamp: Date.now(),
    };

    try {
      // 1. Save comment (Matching your rules: communityPostComments)
      const commentRef = push(ref(database, `communityPostComments/${communityId}/${commentThread.id}`));
      await set(commentRef, commentData);
      
      // 2. Increment commentCount on parent post (Optional/Background)
      try {
        const parentMsgRef = ref(database, `communityMessages/${communityId}/${commentThread.id}`);
        const parentSnap = await get(parentMsgRef);
        if (parentSnap.exists()) {
          const currentCount = parentSnap.val().commentCount || 0;
          await update(parentMsgRef, { commentCount: currentCount + 1 });
        }
      } catch (countError) {
        console.warn("Could not update parent comment count:", countError);
      }

    } catch (error) {
      console.error("FATAL ERROR in handlePostComment:", error);
      setNewComment(commentText); // Restore text on failure
      window.showToast("Failed to post comment. Check permission.", "error");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId || !commentThread || !communityId) return;

    try {
      // 1. Remove the comment
      const commentRef = ref(database, `communityPostComments/${communityId}/${commentThread.id}/${commentId}`);
      await set(commentRef, null);

      // 2. Decrement commentCount on parent post
      try {
        const parentMsgRef = ref(database, `communityMessages/${communityId}/${commentThread.id}`);
        const parentSnap = await get(parentMsgRef);
        if (parentSnap.exists()) {
          const currentCount = parentSnap.val().commentCount || 0;
          await update(parentMsgRef, { commentCount: Math.max(0, currentCount - 1) });
        }
      } catch (countError) {
        console.warn("Could not update parent comment count:", countError);
      }
      
      window.showToast("Comment deleted", "success");
    } catch (error) {
      console.error("Error deleting comment:", error);
      window.showToast("Failed to delete comment", "error");
    }
  };



  if (!community || messagesLoading) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  return (
    <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white relative overflow-hidden">
      <div className="flex h-full">
        <Sidebar />

        <main className="flex-1 h-full flex flex-col bg-white dark:bg-[#1A1A1A] relative min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-8 gap-2 sm:gap-4 border-b dark:border-[#2A2A2A] border-[#D9D9D9]/50 pb-3">
            <div className="flex items-center min-w-0 flex-1">
              <button
                onClick={handleGoBack}
                className="flex items-center justify-center p-1.5 sm:p-2 flex-shrink-0"
              >
                <img
                  src={ICONS.arrowBack}
                  className="w-4 h-4 sm:w-5 sm:h-5 brightness-0 dark:invert"
                  alt="Back"
                />
              </button>
              <div className="flex items-center min-w-0 ml-1 sm:ml-2 md:ml-3 flex-1">
                <button
                  onClick={() =>
                    navigate(`${ROUTES.VIEW_PROFILE}/${community.hostId}`)
                  }
                >
                  <div className="relative w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
                    {!loadedImages[`header-${communityId}`] && (
                      <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
                    )}
                    <img
                      src={
                        community.imageUrl ||
                        "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"
                      }
                      alt={community.name}
                      className={`w-full h-full rounded-full object-cover cursor-pointer transition-opacity duration-300 ${
                        loadedImages[`header-${communityId}`] ? "opacity-100" : "opacity-0"
                      }`}
                      onLoad={() => setLoadedImages(prev => ({ ...prev, [`header-${communityId}`]: true }))}
                      onClick={() => setSelectedImage(community.imageUrl)}
                    />
                  </div>
                </button>
                <div className="flex flex-col min-w-0 ml-2 sm:ml-3">
                  <button
                    onClick={() =>
                      navigate(`${ROUTES.VIEW_PROFILE}/${community.hostId}`)
                    }
                  >
                    <p className="font-bold text-sm sm:text-base md:text-lg dark:text-white text-black truncate">
                      {community.name}
                    </p>
                  </button>
                  <button
                    className="flex items-center"
                    onClick={() => setIsMembersModalOpen(true)}
                  >
                    <p className="text-xs sm:text-sm dark:text-white/40 text-primary truncate cursor-pointer">
                      {community.memberCount || 0}{" "}
                      {community.memberCount === 1 ? "member" : "members"}
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {currentUser?.uid && !isHost && (
                <button
                  onClick={handleFollow}
                  className={`py-1 px-2 sm:px-3 md:px-4 text-xs sm:text-sm md:text-[15px] rounded-md whitespace-nowrap ${isFollowing ? "bg-gray-600 text-white" : "bg-primary text-white"}`}
                >
                  {isFollowing ? "Joined" : "Join"}
                </button>
              )}
              {isHost && (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="p-1.5 sm:p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                  >
                    <BsThreeDotsVertical className="w-4 h-4 sm:w-5 sm:h-5 text-gray-800 dark:text-white" />
                  </button>
                  {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white dark:bg-[#2A2A2A] rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                      <ul className="py-1">
                        {menuItems.map((item, index) => (
                          <li key={index}>
                            <button
                              onClick={item.onClick}
                              className="w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500/30 whitespace-nowrap"
                            >
                              {item.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Chat Content */}
          <div
            ref={setContainerRef}
            onScroll={handleScroll}
            className={`flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 md:px-6 py-4 sm:py-6 mt-3 ${isKeyboardOpen ? "pb-20" : "pb-36"} md:pb-0`}
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.9)), url(${IMAGES.chatBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {!noMoreMessages && (
              <div className="flex justify-center items-center py-2 text-white/70 text-sm">
                {pullState.loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Loading...
                  </div>
                ) : pullState.ready ? (
                  "Release to load more"
                ) : (
                  "Pull to load more"
                )}
              </div>
            )}
            {formattedGroupedMessages.map(({ date, messages }) => (
              <div key={date} className="mb-4 sm:mb-6">
                <div className="flex items-center justify-center mb-2 sm:mb-3">
                  <div className="bg-white/20 text-white px-4 sm:px-6 md:px-8 py-1 rounded-lg text-xs sm:text-sm font-medium backdrop-blur-md">
                    {date}
                  </div>
                </div>

                <div className="flex flex-col z-10 relative">
                  {messages.map((message) => {
                    let isFromHost = false;
                    if (currentUser?.uid) {
                      isFromHost = message.senderId === currentUser.uid;
                    }

                    return (
                      <div
                        key={message.id}
                        data-message-id={message.id}
                        className={`flex items-start ${isFromHost ? "justify-end" : "justify-start"}`}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openReactionBox(
                            message.id,
                            e.clientX,
                            e.clientY,
                            message,
                          );
                        }}
                        onTouchStart={(e) => {
                          const touch = e.touches[0];
                          if (longPressTimeout.current) {
                            clearTimeout(longPressTimeout.current);
                          }
                          longPressTimeout.current = setTimeout(() => {
                            longPressTriggered.current = true;
                            openReactionBox(
                              message.id,
                              touch.clientX,
                              touch.clientY,
                              message,
                            );
                          }, 600);
                        }}
                        onTouchMove={(e) => {
                          const touch = e.touches[0];
                          const dx = Math.abs(touch.clientX - 0);
                          const dy = Math.abs(touch.clientY - 0);
                          if (dx > 10 || dy > 10) {
                            isScrollingRef.current = true;
                            clearTimeout(longPressTimeout.current);
                          }
                        }}
                        onTouchEnd={() => {
                          clearTimeout(longPressTimeout.current);
                          if (
                            isReactingRef.current ||
                            longPressTriggered.current ||
                            isScrollingRef.current
                          ) {
                            isReactingRef.current = false;
                            longPressTriggered.current = false;
                            isScrollingRef.current = false;
                            return;
                          }
                          if (message.imageUrl) {
                            setSelectedImage(message.imageUrl);
                          }
                          isScrollingRef.current = false;
                          longPressTriggered.current = false;
                        }}
                        onTouchCancel={() => {
                          clearTimeout(longPressTimeout.current);
                          isScrollingRef.current = false;
                        }}
                      >
                        <MessageBubble
                          message={message}
                          isFromCurrentUser={isFromHost}
                          currentUserId={currentUser?.uid}
                          senderName={message.senderName}
                          senderAvatar={
                            message.senderAvatar ||
                            "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"
                          }
                          showHeader={message.showHeader}
                          onAvatarClick={() =>
                            navigate(
                              `${ROUTES.VIEW_PROFILE}/${message.senderId}`,
                            )
                          }
                          onImageClick={() =>
                            message.imageUrl &&
                            setSelectedImage(message.imageUrl)
                          }
                          selectionMode={selectionMode}
                          selected={selectedMessage?.id === message.id}
                          onToggleReaction={toggleReaction}
                          onReply={() => setCommentThread(message)}
                          chatType="community"
                          totalMembers={community?.memberCount}
                          loadedImages={loadedImages[message.id]}
                          onImageLoad={() =>
                            setLoadedImages((prev) => ({
                              ...prev,
                              [message.id]: true,
                            }))
                          }
                          renderCustomContent={(msg) =>
                            msg.type === "poll" &&
                            msg.pollId && (
                              <div
                                className={`mt-1 sm:mt-2 ${isFromHost ? "ml-auto" : ""}`}
                              >
                                <div
                                  className={`rounded-[1.1rem] ${selectionMode && selectedMessage?.id === msg.id ? "border-2 border-[#57a1be]" : ""}`}
                                >
                                  <PollMessage
                                    communityId={communityId}
                                    pollId={msg.pollId}
                                    currentUser={currentUser}
                                    isDark
                                    isHost={isFromHost}
                                  />
                                </div>
                                <p className="mt-1 text-right text-xs text-gray-400">
                                  {formatTime(msg.timestamp)}
                                </p>
                              </div>
                            )
                          }
                        />

                        {/* Reaction Box */}
                        {selectedMessageId === message.id && (
                          <ReactionBox
                            position={reactionPosition}
                            isFromCurrentUser={isFromHost}
                            message={message}
                            currentUserId={currentUser.uid}
                            onReact={handleReaction}
                            onReply={() => {
                                closeReactionBox();
                                setCommentThread(message);
                            }}
                            onEdit={handleEditMessage}
                            onDelete={handleDeleteMessage}
                            onClose={closeReactionBox}
                            boxRef={reactionBoxRef}
                            isHost={isHost}
                            showReply={true}
                            type={"community"}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Comments Drawer Overlay */}
          {commentThread && (
            <div 
              className="fixed inset-0 bg-black/40 z-[100] flex items-end justify-center sm:p-4"
              onClick={closeComments}
            >
              <div 
                className="bg-white dark:bg-[#1A1A1A] w-full max-w-2xl rounded-t-3xl sm:rounded-3xl flex flex-col max-h-[90vh] shadow-2xl animate-slide-up"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drawer Header */}
                <div className="p-4 border-b dark:border-[#2A2A2A] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={closeComments} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                      <img src={ICONS.arrowBack} className="w-5 h-5 brightness-0 dark:invert rotate-[-90deg] sm:rotate-0" alt="Close" />
                    </button>
                    <h3 className="font-bold text-lg">Comments</h3>
                  </div>
                  <span className="text-sm text-gray-500">{comments.length} comments</span>
                </div>

                {/* Original Post Preview */}
                <div className="p-4 bg-gray-50 dark:bg-[#222] border-b dark:border-[#2A2A2A] opacity-80">
                  <div className="flex items-start gap-3">
                    <img src={commentThread.senderAvatar || IMAGES.avatar} className="w-8 h-8 rounded-full" alt="" />
                    <div>
                      <p className="text-xs font-bold text-primary">{commentThread.senderName}</p>
                      <p className="text-sm line-clamp-2">{commentThread.text || "📷 Photo"}</p>
                    </div>
                  </div>
                </div>

                {/* Comments List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px]">
                  {isCommentsLoading ? (
                    <div className="flex justify-center items-center h-full">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2">
                      <svg className="w-12 h-12 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                      <p>No comments yet. Be the first to say something!</p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="flex items-start gap-3">
                        <img 
                          src={comment.senderAvatar || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"} 
                          className="w-8 h-8 rounded-full object-cover" 
                          alt="" 
                        />
                        <div className="flex-1 bg-gray-100 dark:bg-[#2A2A2A] rounded-2xl rounded-tl-none p-3 shadow-sm">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-bold text-primary">{comment.senderName}</span>
                            <span className="text-[10px] opacity-50">{formatTime(comment.timestamp)}</span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{comment.text}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Comment Input */}
                <div className="p-4 border-t dark:border-[#2A2A2A] bg-white dark:bg-[#1A1A1A] pb-8 sm:pb-4">
                  <div className="flex items-center gap-3">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 bg-gray-100 dark:bg-[#2A2A2A] rounded-2xl p-3 text-sm focus:outline-none resize-none max-h-32"
                      rows="1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handlePostComment();
                        }
                      }}
                    />
                    <button 
                      onClick={handlePostComment}
                      disabled={!newComment.trim()}
                      className="bg-primary text-white p-3 rounded-full disabled:opacity-50 hover:scale-105 transition-transform"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Message Input */}
          {isHost && (
            <div
              className={`dark:border-t border-none border-[#2A2A2A] bg-white dark:bg-[#1A1A1A] p-4 pb-6 md:pb-4 fixed ${isKeyboardOpen ? "bottom-0" : "bottom-18"} md:static left-0 right-0 z-10`}
            >
              <div className="flex items-center gap-1 sm:gap-2 md:gap-4 max-w-full relative">
                {/* Attachment Button */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() =>
                      setIsAttachmentMenuOpen(!isAttachmentMenuOpen)
                    }
                    className="p-1.5 sm:p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30 text-gray-800 dark:text-white"
                  >
                    <CgAttachment className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>

                  {isAttachmentMenuOpen && (
                    <div className="absolute bottom-10 sm:bottom-12 left-0 w-48 md:w-64 bg-white dark:bg-[#2A2A2A] rounded-xl shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-4 gap-1 sm:gap-2 p-1 sm:p-2">
                        <button
                          onClick={() => {
                            imageInputRef.current?.click();
                            setIsAttachmentMenuOpen(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                          disabled={isUploading}
                        >
                          <FiCamera className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                          <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">
                            Image
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            videoInputRef.current?.click();
                            setIsAttachmentMenuOpen(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                        >
                          <FiVideo className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                          <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">
                            Video
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            audioInputRef.current?.click();
                            setIsAttachmentMenuOpen(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                        >
                          <FiUpload className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                          <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">
                            Audio
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            setOpenPollModal(true);
                            setIsAttachmentMenuOpen(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                        >
                          <MdPoll className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                          <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">
                            Poll
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mention Suggestions */}
                {showMentions && mentionSuggestions.length > 0 && (
                  <div
                    className="absolute bottom-full left-10 md:left-16 mb-2 w-48 md:w-64 bg-white dark:bg-[#2A2A2A] rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[9999]"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {mentionSuggestions.map((member) => (
                      <div
                        key={member.uid}
                        className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => handleMentionSelect(member)}
                      >
                        <img
                          src={
                            member.profilePic ||
                            "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"
                          }
                          alt={member.name}
                          className="w-5 h-5 md:w-6 md:h-6 rounded-full object-cover"
                        />
                        <span className="flex-1 min-w-0 text-xs md:text-sm text-gray-800 dark:text-white truncate">
                          {member.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <MessageInput
                  ref={textareaRef}
                  value={editingMessage ? editMessageText : newMessage}
                  onChange={(e) => {
                    if (editingMessage) {
                      setEditMessageText(e.target.value);
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (showMentions && mentionSuggestions.length > 0) {
                        handleMentionSelect(mentionSuggestions[0]);
                      } else {
                        const { selectionStart, selectionEnd } = e.target;
                        const value = editingMessage
                          ? editMessageText
                          : newMessage;
                        const newValue =
                          value.substring(0, selectionStart) +
                          "\n" +
                          value.substring(selectionEnd);

                        if (editingMessage) {
                          setEditMessageText(newValue);
                        } else {
                          setNewMessage(newValue);
                        }

                        setTimeout(() => {
                          e.target.selectionStart = e.target.selectionEnd =
                            selectionStart + 1;
                        }, 0);
                      }
                      autoResizeTextarea(e.target);
                    }
                  }}
                  placeholder={isHost ? "Type a message..." : "Only the host can post here..."}
                  isUploading={isUploading}
                  onCameraClick={() => imageInputRef.current.click()}
                  onSend={() =>
                    editingMessage ? handleUpdateMessage() : handleSendMessage()
                  }
                  onRecordStart={startRecording}
                  isRecording={isVoiceRecording}
                  replyingTo={replyingTo}
                  onClearReply={clearReply}
                  currentUserId={currentUser.uid}
                  senderName={
                    replyingTo?.senderId === currentUser.uid
                      ? "You"
                      : replyingTo?.senderName
                  }
                  showCamera={false}
                />

                {/* Hidden file inputs for attachments */}
                <input
                  type="file"
                  ref={imageInputRef}
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) handleImageUpload(e.target.files[0]);
                    e.target.value = null; // allow re-selecting the same file again
                  }}
                  className="hidden"
                  disabled={isUploading}
                />
                <input
                  type="file"
                  ref={videoInputRef}
                  accept="video/*"
                  onChange={(e) => {
                    if (e.target.files[0]) handleVideoUpload(e.target.files[0]);
                    e.target.value = null;
                  }}
                  className="hidden"
                  disabled={isUploading}
                />
                <input
                  type="file"
                  ref={audioInputRef}
                  accept="audio/*"
                  onChange={(e) => {
                    if (e.target.files[0])
                      handleAudioFileUpload(e.target.files[0]);
                    e.target.value = null;
                  }}
                  className="hidden"
                  disabled={isUploading}
                />
              </div>

              {recordingPopup && (
                <RecordingPopup
                  onCancel={cancelRecording}
                  onSend={async () => {
                    await stopRecording({
                      isHost: currentUser?.uid === community?.hostId,
                      type: replyingTo ? 'comment' : 'post',
                    });
                    const communityRef = ref(database, `Communities/${communityId}`);
                    const communitySnap = await get(communityRef);
                    const communityData = communitySnap.val();
                    const lastActivity = communityData?.lastActivity && communityData.lastActivity !== ""
                      ? Number(communityData.lastActivity) : null;
                    const streakData = calculateStreakWithFreeze(lastActivity, communityData?.streakCount ?? 0, communityData?.freezeUsed ?? 0, 2);
                    setTimeout(() => update(communityRef, { streakCount: streakData.streakCount, freezeUsed: streakData.freezeUsed, lastActivity: Date.now() }), 500);
                  }}
                />
              )}
            </div>
          )}
        </main>

        {/* Comments Drawer Overlay */}
        {commentThread && (
          <div 
            className="fixed inset-0 bg-black/40 z-[100] flex items-end justify-center sm:p-4"
            onClick={closeComments}
          >
            <div 
              className="bg-white dark:bg-[#1A1A1A] w-full max-w-2xl rounded-t-3xl sm:rounded-3xl flex flex-col max-h-[90vh] shadow-2xl animate-slide-up"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Header */}
              <div className="p-4 border-b dark:border-[#2A2A2A] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={closeComments} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                    <img src={ICONS.arrowBack} className="w-5 h-5 brightness-0 dark:invert rotate-[-90deg] sm:rotate-0" alt="Close" />
                  </button>
                  <h3 className="font-bold text-lg">Comments</h3>
                </div>
                <span className="text-sm text-gray-500">{comments.length} comments</span>
              </div>

              {/* Original Post Preview */}
              <div className="p-4 bg-gray-50 dark:bg-[#222] border-b dark:border-[#2A2A2A] opacity-80">
                <div className="flex items-start gap-3">
                  <div className="relative w-8 h-8 flex-shrink-0">
                    {!loadedImages[`drawer-post-${commentThread.id}`] && (
                      <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
                    )}
                    <img 
                      src={commentThread.senderAvatar || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"} 
                      className={`w-full h-full rounded-full transition-opacity duration-300 ${loadedImages[`drawer-post-${commentThread.id}`] ? "opacity-100" : "opacity-0"}`} 
                      onLoad={() => setLoadedImages(prev => ({ ...prev, [`drawer-post-${commentThread.id}`]: true }))}
                      alt="" 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-primary truncate">{commentThread.senderName}</p>
                    <p 
                      className="text-sm whitespace-pre-wrap break-words"
                      style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                    >
                      {(commentThread.text && commentThread.text.length > 250 && !isPreviewExpanded) 
                          ? commentThread.text.slice(0, 250) + "..." 
                          : (commentThread.text || "📷 Photo")}
                    </p>
                    {commentThread.text && commentThread.text.length > 250 && (
                      <button 
                          onClick={(e) => {
                              e.stopPropagation();
                              setIsPreviewExpanded(!isPreviewExpanded);
                          }} 
                          className="text-primary dark:text-[#57a1be] text-[13px] font-semibold mt-1 hover:underline"
                      >
                          {isPreviewExpanded ? "Read less" : "Read more"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px]">
                {isCommentsLoading ? (
                  <div className="flex justify-center items-center h-full">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2">
                    <svg className="w-12 h-12 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                    <p>No comments yet. Be the first to say something!</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id}>
                      <CommentItem
                        comment={comment}
                        currentUser={currentUser}
                        formatTime={formatTime}
                        handleDeleteComment={handleDeleteComment}
                        loadedImages={loadedImages}
                        setLoadedImages={setLoadedImages}
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Comment Input */}
              <div className="p-4 border-t dark:border-[#2A2A2A] bg-white dark:bg-[#1A1A1A] pb-8 sm:pb-4">
                <div className="flex items-center gap-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 bg-gray-100 dark:bg-[#2A2A2A] rounded-2xl p-3 text-sm focus:outline-none resize-none max-h-32"
                    rows="1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handlePostComment();
                      }
                    }}
                  />
                  <button 
                    onClick={handlePostComment}
                    disabled={!newComment.trim()}
                    className="bg-primary text-white p-3 rounded-full disabled:opacity-50 hover:scale-105 transition-transform"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals and Overlays */}
      <CreatePoll
        open={openPollModal}
        onClose={() => setOpenPollModal(false)}
        onCreate={createPoll}
      />

      <ViewMembers
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        membersPath={`Communities/${communityId}/members`}
        hostId={community?.hostId}
        title="Community Members"
      />

      {isUploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1A1A1A] p-4 sm:p-6 rounded-2xl flex flex-col items-center gap-3 sm:gap-4 shadow-lg">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 border-4 border-gray-300 border-t-[#327691] rounded-full animate-spin"></div>
            <span className="text-sm sm:text-base text-gray-800 dark:text-white">
              Sending... {Math.round(uploadProgress)}%
            </span>
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <img
            src={selectedImage}
            alt="Full view"
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-lg object-contain"
          />
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-3 sm:top-5 right-3 sm:right-5 text-white text-2xl sm:text-3xl font-bold"
          >
            ✕
          </button>
          <button
            onClick={() => handleDownloadImage(selectedImage)}
            className="absolute bottom-5 right-5 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition"
            title="Download image"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4"
              />
            </svg>
          </button>
        </div>
      )}

      {invitePopupOpen && community && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="bg-white dark:bg-[#1A1A1A] p-4 sm:p-6 rounded-xl w-full max-w-sm sm:max-w-md shadow-lg relative">
            <button
              onClick={() => {
                setInvitePopupOpen(false);
                clearInviteParam();
              }}
              className="flex justify-self-end mb-2 text-gray-500 hover:text-gray-700 dark:hover:text-white text-xl font-bold"
            >
              ✕
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-center mb-3 sm:mb-4 dark:text-white">
              You've been invited to join {community.name}
            </h2>
            <p className="text-center mb-4 sm:mb-6 text-sm sm:text-base text-gray-600 dark:text-gray-300">
              Click below to join the community.
            </p>
            <button
              onClick={async () => {
                await handleInviteJoin(inviteToken);
                setInvitePopupOpen(false);
              }}
              className="w-full bg-primary text-white py-1.5 sm:py-2 rounded-lg hover:bg-primary/90 transition text-sm sm:text-base"
            >
              Join Community
            </button>
          </div>
        </div>
      )}

      {/* Forward Share Popup */}
      <ForwardSharePopup
        isOpen={isSharePopupOpen}
        onClose={() => setIsSharePopupOpen(false)}
        onShare={handleForwardShare}
        groupId={communityId}
        currentGroupName={community?.name}
      />

      <SignInPopup
        open={showSignInPopup}
        onClose={() => setShowSignInPopup(false)}
      />
    </div>
  );
};

export default CommunityChat;
